export default function Footer() {
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-slate-800 text-white pt-16 pb-8" aria-label="Site footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          <div>
            <div className="mb-6">
              <span className="text-2xl font-bold text-white">Indoor-Restore</span>
              <span className="text-2xl font-bold text-teal-400"> Services</span>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              Professional <strong>water damage restoration</strong> services in Merced, CA. Available 24/7 for emergency response across Merced County and the Central Valley.
            </p>
            <div className="flex space-x-3">
              <a href="#" rel="nofollow noopener" target="_blank" aria-label="Facebook" className="w-10 h-10 bg-slate-700 rounded-lg flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer">
                <i className="ri-facebook-fill text-xl"></i>
              </a>
              <a href="#" rel="nofollow noopener" target="_blank" aria-label="Twitter" className="w-10 h-10 bg-slate-700 rounded-lg flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer">
                <i className="ri-twitter-fill text-xl"></i>
              </a>
              <a href="#" rel="nofollow noopener" target="_blank" aria-label="Instagram" className="w-10 h-10 bg-slate-700 rounded-lg flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer">
                <i className="ri-instagram-line text-xl"></i>
              </a>
              <a href="#" rel="nofollow noopener" target="_blank" aria-label="Yelp" className="w-10 h-10 bg-slate-700 rounded-lg flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer">
                <i className="ri-star-smile-line text-xl"></i>
              </a>
            </div>
          </div>

          <nav aria-label="Quick links">
            <h3 className="text-lg font-bold mb-6">Quick Links</h3>
            <ul className="space-y-3">
              {[
                { label: 'Services', id: 'services' },
                { label: 'About Us', id: 'about' },
                { label: 'Why Choose Us', id: 'why-choose-us' },
                { label: 'Service Areas', id: 'service-areas' },
                { label: 'Testimonials', id: 'testimonials' },
                { label: 'FAQ', id: 'faq' },
                { label: 'Contact', id: 'contact' },
              ].map((item) => (
                <li key={item.id}>
                  <a
                    href={`#${item.id}`}
                    onClick={(e) => { e.preventDefault(); scrollToSection(item.id); }}
                    className="text-slate-400 hover:text-teal-400 transition-colors text-sm cursor-pointer"
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div>
            <h3 className="text-lg font-bold mb-6">Our Services</h3>
            <ul className="space-y-3">
              {[
                'Water Extraction',
                'Structural Drying',
                'Mold Remediation',
                'Flood Damage Restoration',
                'Fire & Smoke Damage',
                'Emergency Repairs',
              ].map((service) => (
                <li key={service}>
                  <a
                    href="#services"
                    onClick={(e) => { e.preventDefault(); scrollToSection('services'); }}
                    className="text-slate-400 hover:text-teal-400 transition-colors text-sm cursor-pointer"
                  >
                    {service}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <address className="not-italic">
            <h3 className="text-lg font-bold mb-6">Contact Us</h3>
            <ul className="space-y-4">
              <li className="flex items-start">
                <i className="ri-phone-line text-teal-400 mr-3 mt-1 flex-shrink-0"></i>
                <div>
                  <a href="tel:2094512288" className="text-slate-400 hover:text-teal-400 transition-colors text-sm cursor-pointer font-semibold">
                    (209) 451-2288
                  </a>
                  <p className="text-xs text-slate-500">24/7 Emergency Line</p>
                </div>
              </li>
              <li className="flex items-start">
                <i className="ri-mail-line text-teal-400 mr-3 mt-1 flex-shrink-0"></i>
                <a href="mailto:info@mercedwaterrestoration.com" className="text-slate-400 hover:text-teal-400 transition-colors text-sm cursor-pointer break-all">
                  info@mercedwaterrestoration.com
                </a>
              </li>
              <li className="flex items-start">
                <i className="ri-map-pin-line text-teal-400 mr-3 mt-1 flex-shrink-0"></i>
                <span className="text-slate-400 text-sm">
                  1729 U St, Merced, CA 95340<br />
                  <span className="text-slate-500 text-xs">Serving Merced County &amp; Central Valley</span>
                </span>
              </li>
              <li className="flex items-start">
                <i className="ri-time-line text-teal-400 mr-3 mt-1 flex-shrink-0"></i>
                <span className="text-slate-400 text-sm">
                  Open 24 Hours · 7 Days a Week<br />
                  <span className="text-teal-400 text-xs font-semibold">Always Available for Emergencies</span>
                </span>
              </li>
            </ul>
          </address>
        </div>

        <div className="border-t border-slate-700 pt-8 pb-6">
          <div className="flex flex-wrap justify-center gap-6 mb-8">
            {[
              { icon: 'ri-shield-check-line', label: 'Licensed & Insured' },
              { icon: 'ri-award-line', label: 'IICRC Certified' },
              { icon: 'ri-time-line', label: '24/7 Emergency' },
              { icon: 'ri-customer-service-2-line', label: 'Insurance Assistance' },
              { icon: 'ri-map-pin-2-line', label: 'Merced, CA Local' },
            ].map((badge) => (
              <div key={badge.label} className="flex items-center gap-2 text-slate-400 text-xs">
                <i className={`${badge.icon} text-teal-400 text-base`}></i>
                <span>{badge.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-slate-700 pt-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-xs text-center md:text-left">
              © {new Date().getFullYear()} Indoor-Restore Services. All rights reserved. | Serving Merced, CA &amp; the Central Valley
            </p>
            <div className="flex space-x-6">
              <a href="#" className="text-slate-500 hover:text-teal-400 transition-colors text-xs cursor-pointer">
                Privacy Policy
              </a>
              <a href="#" className="text-slate-500 hover:text-teal-400 transition-colors text-xs cursor-pointer">
                Terms of Service
              </a>
              <a href="#" className="text-slate-500 hover:text-teal-400 transition-colors text-xs cursor-pointer">
                Sitemap
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
